<script setup lang="ts">
import { i18n } from '@/i18n';
import { computed, onMounted, ref } from 'vue';
import InventoryDashboardChildLayout from './InventoryDashboardChildLayout.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { useRouter } from 'vue-router';
import { INVENTORY_DASHBOARD_PATH } from '../constants';
import { Kpi } from '../domains/entities/Kpi';
import { storeToRefs } from 'pinia';
import { useInventoryDashboardStore } from './useInventoryDashboardStore';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import { UserAccount } from '@/coreDomainsModule/entities/users';
import DecimalNumberInput from '@/corePagesModule/components/DecimalNumberInput.vue';
import { KpiFactory } from '../domains/factories/KpiFactory';
import { DimensionTypes, MetricTypes } from '../domains/entities/Dashboard';
import { UNASSIGNED_COLUMN } from '@/coreDomainsModule/constants';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';

const t = i18n.t;
const router = useRouter();

const store = useInventoryDashboardStore();
const { getKpiData, getAggregateBusinessResponsibleUsersForKpi, postKpiData } = store;

const kpiData = ref<Kpi[]>([]);
const userData = ref<UserAccount[]>([]);

const kpiWithUser = computed((): Array<UserAccount & { kpi: Kpi | undefined }> => {
  console.log(userData.value);
  return userData.value.map((u) => {
    return {
      ...u,
      kpi: kpiData.value.find((k) => k.dimensions[0].dimensionValue.code === u.email && k.dimensions[0].dimensionValue.name === u.name),
    };
  });
});

/**
 * KPI入力値をユーザー単位で更新する
 * @param value - 入力されたKPI値
 * @param index - 対象ユーザーの行インデックス
 */
const inputKpiHander = (value: number, index: number) => {
  const target = kpiWithUser.value[index];
  if (target.kpi) {
    const targetKpiData = kpiData.value.find(
      (k) => k.dimensions[0].dimensionValue.code === target.email && k.dimensions[0].dimensionValue.name === target.name
    );
    if (!targetKpiData) return;
    targetKpiData.kpiValue = value;
  } else {
    kpiData.value.push(
      KpiFactory.create({
        metricType: MetricTypes.Turnover,
        kpiValue: value,
        dimensions: [
          {
            dimensionType: DimensionTypes.AggregateBusinessResponsibleUser,
            dimensionValue: {
              code: target.email,
              name: target.name,
            },
          },
        ],
      })
    );
  }
};

/**
 * 戻るボタンを押下時ハンドラ
 */
const handleOnClickBack = () => {
  router.push(INVENTORY_DASHBOARD_PATH);
};

/**
 * 確定ボタン押下時ハンドラ
 */
const handleOnClickSave = async () => {
  const res = await postKpiData(kpiData.value);
  if (res) {
    router.push(INVENTORY_DASHBOARD_PATH);
  } else {
    await showInfoModal({
      modalTitle: t('KPI保存時にエラーが発生しました。'),
      modalDescription: t('リロードして再度お試しください。'),
      backButtonLabel: t('OK'),
      isDisabledNextButton: true,
    });
    return;
  }
};

onMounted(async () => {
  const [resKpiData, resUserData] =
    (await waitLoading(Promise.all([getKpiData(), getAggregateBusinessResponsibleUsersForKpi()]), undefined, '')) ?? [];
  if (resKpiData == null || resUserData == null) {
    await showInfoModal({
      modalTitle: t('KPI取得時にエラーが発生しました。'),
      modalDescription: t('リロードして再度お試しください。'),
      backButtonLabel: t('OK'),
      isDisabledNextButton: true,
    });
    return;
  }
  kpiData.value = resKpiData;
  userData.value = resUserData;
});
</script>
<template>
  <InventoryDashboardChildLayout>
    <div class="kpi-setting">
      <div class="kpi-setting__header">
        <div class="kpi-setting__header__title">{{ t('仕入担当者の在庫回転率KPIの設定') }}</div>
        <div class="kpi-setting__header__desc">{{ t('担当者ごとに目標とする在庫回転率（ヶ月）を記入してください') }}</div>
      </div>
      <div class="kpi-setting__setting">
        <div class="kpi-setting__setting__row kpi-setting__setting__row--border">
          <div>{{ t('仕入担当者') }}</div>
          <div>{{ t('在庫回転率') }}</div>
        </div>
        <div v-for="(value, index) in kpiWithUser" :key="index" class="kpi-setting__setting__row">
          <div>{{ value.name || t(UNASSIGNED_COLUMN) }}</div>
          <div>
            <DecimalNumberInput
              :model-value="value.kpi?.kpiValue"
              :placeholder="t('例: 1.2')"
              @update:model-value="(e) => inputKpiHander(e, index)"
            />
          </div>
        </div>
      </div>
      <div class="kpi-setting__button">
        <TextButton button-style="btn-secondary" @click="handleOnClickBack">{{ t('戻る') }}</TextButton>
        <TextButton button-style="btn-primary" @click="handleOnClickSave">{{ t('保存') }}</TextButton>
      </div>
    </div>
  </InventoryDashboardChildLayout>
</template>
<style lang="scss" scoped>
.kpi-setting {
  width: max-content;
  display: grid;
  padding: 8px 16px 24px 16px;
  border-radius: 4px;
  background-color: $semanticWhite;
  border: 1px solid $borderDefault;
  gap: 16px;
  &__header {
    display: grid;
    padding: 8px 0;
    gap: 8px;
    color: $textDefault;
    &__title {
      display: grid;
      height: 24px;
      grid-template-columns: max-content max-content;
      align-items: center;
      gap: 8px;
      font-weight: 700;
      font-size: 16px;
      line-height: 1;
    }
    &__desc {
      font-weight: 400;
      font-size: 14px;
      line-height: 1.3;
    }
  }
  &__setting {
    display: grid;
    gap: 8px;
    padding-bottom: 8px;
    border-bottom: 1px solid $borderDefault;
    &__row {
      height: 32px;
      display: grid;
      grid-template-columns: repeat(2, 360px);
      align-items: center;
      color: $textSecondary;
      font-size: 16px;
      gap: 48px;
      &--border {
        border-bottom: 1px solid $borderDefault;
      }
    }
  }

  &__button {
    display: grid;
    grid-template-columns: max-content max-content;
    gap: 10px;
    justify-content: center;
  }
}
</style>
